import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'
import useI18n from 'hooks/useI18n'
import { ReadIdentityAccess, RequestUniverseAccess, HydrateIdentitySessions, IDENTITY_AREA_ROUTES } from 'services/identity'
import { exposeStrapiError } from 'utils'
import { industryOptions, optionsCountries } from 'utils/options'
import { getProductAreaFromPath } from 'utils/portal'

export default function useController() {
  const { t } = useI18n()
  const history = useHistory()
  const formRef = useRef()
  const [identity, setIdentity] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(false)
  const load = useCallback(async () => {
    setLoading(true)
    const result = await ReadIdentityAccess()
    const hasError = exposeStrapiError(result)
    if (!result?.centralUserId || hasError) setError(true)
    else { setIdentity(result); setError(false) }
    setLoading(false)
  }, [])
  useEffect(() => { load() }, [load])
  const submit = useCallback(async () => {
    if (saving) return
    const profile = formRef.current?.getForm()
    if (!profile) return
    setSaving(true)
    const result = await RequestUniverseAccess(profile)
    const hasError = exposeStrapiError(result)
    if (result?.id && !hasError) {
      toast.success(t('identity_request_sent'))
      await load()
    } else if (!result) toast.error(t('identity_access_unavailable'))
    setSaving(false)
  }, [load, saving, t])
  const enterArea = useCallback(async area => {
    const session = await HydrateIdentitySessions()
    if (session?.areas?.includes(area)) history.push(IDENTITY_AREA_ROUTES[area])
    else { toast.error(t('identity_access_unavailable')); await load() }
  }, [history, load, t])
  const managementChanged = useCallback(async () => {
    const session = await HydrateIdentitySessions()
    const area = getProductAreaFromPath(history.location.pathname)
    if (session?.areas?.length && !session.areas.includes(area)) history.push(IDENTITY_AREA_ROUTES[session.areas[0]])
    else await load()
  }, [history, load])
  const rows = useMemo(() => (identity?.accesses || []).map(access => ({ ...access, id: access.area,
    label: access.area === 'universe' ? 'MagVenture Universe' : access.area === 'lite' ? 'Universe Lite' : 'Accelerator',
    displayStatus: t(access.enabled ? `identity_status_${access.status}` : 'identity_status_legacy'),
  })), [identity, t])
  const formItems = useMemo(() => [
    { ref: 'firstName', placeholder: t('first_name'), required: true, half: true },
    { ref: 'lastName', placeholder: t('last_name'), required: true, half: true },
    { ref: 'country', placeholder: t('country'), options: optionsCountries, required: true, half: true },
    { ref: 'companyName', placeholder: t('company_name'), half: true },
    { ref: 'jobTitle', placeholder: t('job_title'), half: true },
    { ref: 'industry', placeholder: t('industry'), options: industryOptions, half: true },
    { ref: 'state', placeholder: t('state'), half: true },
    { ref: 'zipcode', placeholder: t('zipcode'), half: true },
    { ref: 'phone', placeholder: t('phone_number'), type: 'phone', full: true },
  ].map(item => ({ ...item, id: `identity-request-${item.ref}` })), [t])
  return { t, identity, rows, formItems, formRef, loading, saving, error, load, submit, enterArea, managementChanged }
}
